import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ACTIVITY_TYPES, eventLine, participantOf, participantsOf } from '../../utils/collab3Model';
import { ACTIVITY_GROUPS, filterActivity, groupByDay, mergeActivity } from '../../utils/workspacePageModel';
import { eventTime } from '../../utils/workspaceDisplay';

const sameTypes = (a, b) => a.length === b.length && a.every((type) => b.includes(type));

/**
 * The workspace's Activity tab (design "Flow Workspace", Activity): one timeline across the workspace's projects, grouped
 * by day, a project tag on each line, filters by project, person and type (and the quick type chips). It reads the same
 * feed as each project's History › Activity (services/CollabService.js getActivity), so nothing is stored twice.
 */
export default function WorkspaceActivity({ workspace, userSub, activity, onOpenProject, onReload }) {
  const { projects } = workspace;
  const [project, setProject] = useState('all');
  const [who, setWho] = useState('all');
  const [types, setTypes] = useState([]);

  const all = useMemo(
    () => mergeActivity(Object.fromEntries(Object.entries(activity).map(([id, entry]) => [id, entry?.value])), projects),
    [activity, projects],
  );
  const people = useMemo(() => participantsOf(all), [all]);
  const shown = useMemo(() => filterActivity(all, { project, who, types }), [all, project, who, types]);
  const days = useMemo(() => groupByDay(shown), [shown]);
  const loading = projects.some((entry) => !activity[entry.projectId] || activity[entry.projectId].status === 'loading');
  const failed = projects.filter((entry) => activity[entry.projectId]?.status === 'error').length;
  const filtered = project !== 'all' || who !== 'all' || types.length > 0;
  const personLabel = (event) => {
    const participant = participantOf(event);
    if (!participant) { return ''; }
    return participant.kind === 'person' && userSub && participant.author?.id === userSub ? 'You' : participant.label || '';
  };

  return (
    <div className="ws-activity">
      <div className="ws-tools">
        <strong className="ws-grow">Everything that happened, all projects mixed</strong>
        <select aria-label="Project" className="ws-input" onChange={(event) => setProject(event.target.value)} value={project}>
          <option value="all">Projects: all</option>
          {projects.map((entry) => <option key={entry.projectId} value={entry.projectId}>{entry.name || entry.projectId}</option>)}
        </select>
        <select aria-label="Person" className="ws-input" onChange={(event) => setWho(event.target.value)} value={who}>
          <option value="all">People: all</option>
          {people.map((person) => <option key={person.key} value={person.key}>{person.kind === 'agent' ? person.label : person.label || 'Someone'}</option>)}
        </select>
        <select aria-label="Type" className="ws-input" onChange={(event) => setTypes(event.target.value === 'all' ? [] : [event.target.value])} value={types.length === 1 ? types[0] : 'all'}>
          <option value="all">Type: all</option>
          {ACTIVITY_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
        </select>
        <button className="ws-btn" onClick={onReload} type="button">Refresh</button>
      </div>
      <div aria-label="Quick type filters" className="ws-chips" role="group">
        {ACTIVITY_GROUPS.map((group) => (
          <button aria-pressed={sameTypes(types, group.types)} className={`ws-chip-btn${sameTypes(types, group.types) ? ' is-on' : ''}`} key={group.id} onClick={() => setTypes(group.types)} type="button">{group.label}</button>
        ))}
      </div>
      {failed > 0 && <p className="ws-muted" role="status">The activity of {failed} project{failed === 1 ? '' : 's'} could not be read.</p>}
      {projects.length === 0 && <p className="workspaces-note">No projects in this workspace yet.</p>}
      {projects.length > 0 && shown.length === 0 && (
        <p className="workspaces-note">
          {loading && all.length === 0 && 'Loading…'}
          {!loading && filtered && 'Nothing matches these filters.'}
          {!loading && !filtered && 'Nothing happened in the last 30 days.'}
          {loading && all.length > 0 && 'Nothing matches these filters yet.'}
        </p>
      )}
      {days.length > 0 && (
        <div className="ws-card ws-timeline">
          {days.map((day) => (
            <section aria-label={day.label} key={day.key}>
              <h2 className="ws-day">{day.label}</h2>
              <ul className="ws-rows">
                {day.events.map((event, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li className="ws-row ws-event" key={`${event.projectId}-${event.id || event.at}-${index}`}>
                    <time className="ws-muted ws-time" dateTime={event.at}>{eventTime(event.at)}</time>
                    <span className="ws-tag" title={event.projectName}>{event.projectName}</span>
                    <span className="ws-grow">{eventLine(event) || event.type}</span>
                    <span className="ws-muted ws-who">{personLabel(event)}</span>
                    <button aria-label={`Open ${event.projectName} activity`} className="ws-btn" onClick={() => onOpenProject(event.projectId, { tab: 'activity' })} type="button">Open</button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

WorkspaceActivity.propTypes = {
  activity: PropTypes.object.isRequired,
  onOpenProject: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  userSub: PropTypes.string,
  workspace: PropTypes.object.isRequired,
};
